<script setup>
import {computed, watch, ref, onMounted} from "vue";
import rules from '@/utils/validationRules.js'
import { vMaska } from "maska/vue"

import ReferralFormLabel from "@/components/ReferralFormLabel.vue";
import AddressAutocomplete from "@/components/AddressAutocomplete.vue";
import ReferralFundingSelector from "@/components/ReferralFundingSelector.vue";
import ReferralContact from "@/components/ReferralContact.vue";
import ReferralYesNoUnsure from "@/components/ReferralYesNoUnsure.vue";
import ReferralDateInput from "@/components/ReferralDateInput.vue";
import axios from "axios";
import * as Sentry from "@sentry/vue";
import ReferralSubmissionError from "@/components/ReferralSubmissionError.vue";

const LOCAL_STORAGE_KEY = "ndisReferralFormState";
const isDevelopment = import.meta.env.DEV
    || document.getElementById('app')?.dataset.isDevelopment === 'true'
const submissionId = ref(crypto.randomUUID())

const formSteps = ref([
  { title: 'Participant Details', value: 1},
  { title: 'Plan Details',        value: 2},
  { title: 'Contacts',            value: 3},
  { title: 'Risk Assessment',     value: 4},
  { title: 'Confirm & Submit',    value: 5},
])
const stepper = ref();
const stepperStep = ref(1)
const form1 = ref()
const form2 = ref()
const form3 = ref()
const form4 = ref()

const stepValidity = computed(() => {
  return {
    step1: form1.value && form1.value.isValid,
    step2: form2.value && form2.value.isValid,
    step3: form3.value && form3.value.isValid,
    step4: form4.value && form4.value.isValid,
  }
})

const isNextButtonActive = computed(() => {
  if (stepperStep.value === 1 && stepValidity.value.step1) return true;
  if (stepperStep.value === 2 && stepValidity.value.step2) return true;
  if (stepperStep.value === 3 && stepValidity.value.step3) return true;
  if (stepperStep.value === 4 && stepValidity.value.step4) return true;
  if (stepperStep.value === 5) return rules.optionalEmail(otherConfirmationEmail.value) === true;
  return false;
})

const isSubmittingForm = ref(false);
const successfullySubmitted = ref(false);
const submitError = ref();

const whoFillingFor = ref('')
const filledOnBehalfBy = ref('')
const participantDetails = ref({
  firstName: '',
  lastname: '',
  preferredName: '',
  dateOfBirth: undefined,
  ndisNumber: '',
  gender: undefined,
  mobile: '',
  phone: '',
  email: '',
  address: undefined,
  addressPlaceId: undefined,
  addressType: '',
  diagnosis: '',
  reasonForServices: ''
})

const planDetails = ref({
  startDate: undefined,
  endDate: undefined,
  managementType: '',
  isUrgent: undefined,
  urgentDetails: '',
  fundingCategories : [
    { type: 'Improved Health and Wellbeing', amount: '' }
  ]
})

const planManagerContact = ref({
  name: '',
  company: '',
  phone: '',
  email: ''
})

const hasSupportCoord = ref(null)
const supportCoordinatorContact = ref({
  name: '',
  company: '',
  phone: '',
  email: ''
})

const participantSigning = ref(null)
const signatoryContact = ref({
  name: '',
  relation: '',
  phone: '',
  email: ''
})

const participantBooking = ref(null)
const bookingContact = ref({
  name: '',
  relation: '',
  phone: '',
  email: ''
})

const riskAssessment = ref({
  environmentSafe: undefined,
  environmentSafeDetails: undefined,
  dangerousAnimals: undefined,
  dangerousAnimalsDetails: undefined,
  drugUserAttending: undefined,
  drugUserAttendingDetails: undefined,
  participantSmoker: undefined,
  participantSmokerDetails: undefined,
  participantAggression: undefined,
  participantAggressionDetails: undefined,
  participantAggressionTriggers: undefined,
  participantAggressionTriggersDetails: undefined,
  participantStressTriggers: undefined,
  participantStressTriggersDetails: undefined,
  shouldHaveSecondPerson: undefined,
  shouldHaveSecondPersonDetails: undefined,
  anyCriminals: undefined,
  anyCriminalsDetails: undefined,
  anyOtherRelevantInformationDetails: undefined,
})

const confirmationEmails = ref([])
const otherConfirmationEmail = ref('')

const referralErrorShowing = ref(false);

const allContactEmails = computed(() => {
  const emails  = [
    {email: participantDetails.value.email, title: 'Participant'},
    {email: bookingContact.value.email, title: 'Booking Contact'},
    {email: signatoryContact.value.email, title: 'Signatory'},
    {email: supportCoordinatorContact.value.email, title: 'Support Coordinator'},
    {email: planManagerContact.value.email, title: 'Plan Manager'}
  ];

  return emails.filter(email => !!email.email)
})

async function handleNextBtnClick() {
  if (stepperStep.value === formSteps.value.length) {
    await submitReferral();
    return;
  }

  stepper.value.next();
}

function saveFormState() {
  const formState = {
    submissionId: submissionId.value,
    whoFillingFor: whoFillingFor.value,
    filledOnBehalfBy: filledOnBehalfBy.value,
    participantDetails: {
      ...participantDetails.value,
      dateOfBirth: participantDetails.value.dateOfBirth
          ? participantDetails.value.dateOfBirth.toISOString()
          : null,
    },
    planDetails: {
      ...planDetails.value,
      startDate: planDetails.value.startDate
          ? planDetails.value.startDate.toISOString()
          : null,
      endDate: planDetails.value.endDate
          ? planDetails.value.endDate.toISOString()
          : null,
    },
    planManagerContact: planManagerContact.value,
    hasSupportCoord: hasSupportCoord.value,
    supportCoordinatorContact: supportCoordinatorContact.value,
    participantSigning: participantSigning.value,
    signatoryContact: signatoryContact.value,
    participantBooking: participantBooking.value,
    bookingContact: bookingContact.value,
    riskAssessment: riskAssessment.value,
    confirmationEmails: confirmationEmails.value,
    otherConfirmationEmail: otherConfirmationEmail.value
  };
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(formState));
}

function loadFormState() {
  const savedState = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (savedState) {
    const parsedState = JSON.parse(savedState);
    submissionId.value = parsedState.submissionId || crypto.randomUUID();
    whoFillingFor.value = parsedState.whoFillingFor || '';
    filledOnBehalfBy.value = parsedState.filledOnBehalfBy || '';
    participantDetails.value = {
      ...parsedState.participantDetails,
      dateOfBirth: parsedState.participantDetails?.dateOfBirth
          ? new Date(parsedState.participantDetails.dateOfBirth)
          : undefined,
    };
    planDetails.value = {
      ...parsedState.planDetails,
      startDate: parsedState.planDetails?.startDate
          ? new Date(parsedState.planDetails.startDate)
          : undefined,
      endDate: parsedState.planDetails?.endDate
          ? new Date(parsedState.planDetails.endDate)
          : undefined,
    };
    planManagerContact.value = parsedState.planManagerContact || {};
    hasSupportCoord.value = parsedState.hasSupportCoord
    supportCoordinatorContact.value = parsedState.supportCoordinatorContact || {};
    participantSigning.value = parsedState.participantSigning
    signatoryContact.value = parsedState.signatoryContact || {};
    participantBooking.value = parsedState.participantBooking
    bookingContact.value = parsedState.bookingContact || {};
    riskAssessment.value = parsedState.riskAssessment || {};
    confirmationEmails.value = parsedState.confirmationEmails || [];
    otherConfirmationEmail.value = parsedState.otherConfirmationEmail || '';
  }
}

watch(
    [
      whoFillingFor,
      filledOnBehalfBy,
      participantDetails,
      planDetails,
      planManagerContact,
      hasSupportCoord,
      supportCoordinatorContact,
      participantSigning,
      signatoryContact,
      participantBooking,
      bookingContact,
      riskAssessment,
      confirmationEmails,
      otherConfirmationEmail
    ],
    saveFormState,
    { deep: true }
);

function buildReferralSubmission() {
  const data = {
    submissionId: submissionId.value,
    whoFillingFor: whoFillingFor.value,
    filledOnBehalfBy: filledOnBehalfBy.value,
    participantDetails: {
      ...participantDetails.value,
      dateOfBirth: toLocalDateString(participantDetails.value.dateOfBirth)
    },
    planDetails: {
      ...planDetails.value,
      startDate: toLocalDateString(planDetails.value.startDate),
      endDate: toLocalDateString(planDetails.value.endDate)
    },
    planManagerContact: planDetails.value.managementType === 'Plan Managed' ? {...planManagerContact.value} : undefined,
    supportCoordinatorContact: hasSupportCoord.value && hasSupportCoord.value === true ? {...supportCoordinatorContact.value} : undefined,
    hasSupportCoordinator: hasSupportCoord.value,
    signatoryContact: participantSigning.value === false ? {...signatoryContact.value} : undefined,
    participantSigning: participantSigning.value,
    bookingContact:  participantBooking.value === false ? {...bookingContact.value} : undefined,
    participantBooking: participantBooking.value,
    riskAssessment: {
      ...riskAssessment.value,
      additionalInformation: riskAssessment.value.anyOtherRelevantInformationDetails
    },
    confirmationEmails: [...confirmationEmails.value]
  }

  if (otherConfirmationEmail.value) {
    data.confirmationEmails.push(otherConfirmationEmail.value)
  }

  // convert funding details to numbers
  data.planDetails.fundingCategories = data.planDetails.fundingCategories.map(x => {
    return { ...x, amount: Number(x.amount) }
  })

  return data;
}

function toLocalDateString(value) {
  if (!value) return undefined
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function planEndDateIsAfterStartDate() {
  if (!planDetails.value.startDate || !planDetails.value.endDate) return true
  return planDetails.value.endDate > planDetails.value.startDate
      || 'Plan end date must be after the start date.'
}

async function submitReferral() {
  const data = buildReferralSubmission();

  console.log(data)

  isSubmittingForm.value = true
  const result = await postReferralRequest(data)
  
  isSubmittingForm.value = false
  
  if (!result.success) {
    submitError.value = result.error
    referralErrorShowing.value = true
    return;
  }
  successfullySubmitted.value = true
  if (!isDevelopment) {
    localStorage.removeItem(LOCAL_STORAGE_KEY)
  }
}

async function postReferralRequest(referral) {
  try {
    const response = await axios.post('api/referral', referral)
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    Sentry.captureException(error);
    // Handle error response
    return {
      success: false,
      data: error.response?.data || null,
      status: error.response?.status || null,
      error: error.message,
    };
  }
}

onMounted(() => {
  loadFormState();
});

</script>

<template>
  <v-layout>
    <v-main>
      <div class="mx-auto my-5">
        <h1 class="text-blue-grey-darken-3">NDIS Referral Form</h1>
        
      </div>

      <v-stepper
          ref="stepper"
          v-model="stepperStep"
          :items="formSteps"
          flat
          color="secondary"
      >

        <template #item.1>
          <v-form ref="form1" class="mt-4">
            <v-row>
              <v-col cols="12">
                <p>Thank you for considering Dayna Moore Dietetics to achieve your NDIS goals. This referral form is an important step in ensuring we have all the information to efficiently and effectively get your supports right from the get-go.</p>
                <p>It should take roughly 10 minutes to complete all the steps in this form.</p>
                <p><a href="/ndis-dietitian-western-sydney">Click here if you'd like to learn more about our NDIS services</a></p>
                
                <ReferralFormLabel required>Who are you filling this form in for?</ReferralFormLabel>
                <v-radio-group v-model="whoFillingFor" inline hide-details class="text-h6 mt-2" color="primary" :rules="[rules.required]">
                  <v-radio label="Myself" value="myself" class="pr-5"></v-radio>
                  <v-radio label="Behalf of Participant" value="behalf" class="pr-5"></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="12" md="4"  class="py-0" v-if="whoFillingFor === 'behalf'">
                <ReferralFormLabel required>Relation to Participant</ReferralFormLabel>
                <v-text-field v-model="filledOnBehalfBy" required :rules="[rules.required]"/>
              </v-col>
              <v-divider/>
            </v-row>
            <v-row class="mt-3">
              <v-col cols="12" class="text-overline">
                Participant Contact Details
              </v-col>
              <v-col cols="12" md="4"  class="py-0">
                <ReferralFormLabel required>First Name</ReferralFormLabel>
                <v-text-field v-model="participantDetails.firstName" required :rules="[rules.required]"/>
              </v-col>
              <v-col cols="12" md="4"  class="py-0">
                <ReferralFormLabel required>Last Name</ReferralFormLabel>
                <v-text-field v-model="participantDetails.lastname" required :rules="[rules.required]"/>
              </v-col>
              <v-col cols="12" md="4"  class="py-0">
                <ReferralFormLabel>Preferred Name</ReferralFormLabel>
                <v-text-field v-model="participantDetails.preferredName"/>
              </v-col>

              <v-col cols="12" md="4"  class="py-0">
                <ReferralFormLabel required>Date of Birth</ReferralFormLabel>
                <ReferralDateInput required v-model="participantDetails.dateOfBirth" />
              </v-col>
              <v-col cols="12" md="4"  class="py-0">
                <ReferralFormLabel required>Gender</ReferralFormLabel>
                <v-select v-model="participantDetails.gender" required :items="['Male', 'Female', 'Non-Binary', 'Transgender', 'Intersex', 'Prefer not to say']" :rules="[rules.required]"/>
              </v-col>
              <v-col cols="12" md="4"  class="py-0">
                <ReferralFormLabel required>Email</ReferralFormLabel>
                <v-text-field v-model="participantDetails.email" required :rules="[rules.required, rules.email]" type="email" inputmode="email"/>
              </v-col>
              <v-col cols="12" md="4"  class="py-0">
                <ReferralFormLabel required>Mobile</ReferralFormLabel>
                <v-text-field v-model="participantDetails.mobile" required :rules="[rules.required, rules.mobile]" v-maska="'04## ### ###'" inputmode="numeric" type="text"/>
              </v-col>
              <v-col cols="12" md="4"  class="py-0">
                <ReferralFormLabel>Phone</ReferralFormLabel>
                <v-text-field v-model="participantDetails.phone" inputmode="numeric" type="text" v-maska="'###############'" :rules="[rules.optionalPhone]"/>
              </v-col>
              <v-col cols="12" class="py-0">
                <AddressAutocomplete
                    required
                    v-model="participantDetails.address"
                    v-model:place-id="participantDetails.addressPlaceId"/>
              </v-col>
              <v-col cols="12" class="py-0">
                <ReferralFormLabel required>The above address is </ReferralFormLabel>
                <v-radio-group v-model="participantDetails.addressType" inline hide-details class="text-h6 mt-2" color="primary" :rules="[rules.required]">
                  <v-radio label="Private Residence" value="Private Residence" class="pr-5"></v-radio>
                  <v-radio label="Day Program" value="Day Program" class="pr-5"></v-radio>
                  <v-radio label="Group Home" value="Group Home" class="pr-5"></v-radio>
                  <v-radio label="Supported Independent Living (SIL)" value="Supported Independent Living (SIL)" class="pr-5"></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>

            <v-row class="mt-8">
              <v-col cols="12" class="py-0 mb-3 text-overline">
                Participant NDIS Details
              </v-col>
              <v-col cols="12" md="4"  class="py-0">
                <ReferralFormLabel required>NDIS Number</ReferralFormLabel>
                <v-text-field v-model="participantDetails.ndisNumber" required :rules="[rules.required, rules.ndisNumber]" v-maska="'#########'" type="text" inputmode="numeric" pattern="[0-9]*"/>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6"  class="py-0">
                <ReferralFormLabel required>Diagnosis</ReferralFormLabel>
                <v-textarea v-model="participantDetails.diagnosis" required :rules="[rules.required]"/>
              </v-col>
              <v-col cols="12" md="6"  class="pt-0">
                <ReferralFormLabel required>Reason for accessing services</ReferralFormLabel>
                <v-textarea v-model="participantDetails.reasonForServices" required :rules="[rules.required]"/>
              </v-col>
            </v-row>
          </v-form>

        </template>

        <template #item.2>
          <v-form ref="form2" class="mt-4">
            <v-row>
              <v-col cols="12" class="py-0 mb-1 text-overline">
                Plan Dates
              </v-col>
              <v-col cols="12" md="4"  class="py-0">
                <ReferralFormLabel required>Plan start date</ReferralFormLabel>
                <ReferralDateInput required v-model="planDetails.startDate"/>
              </v-col>
              <v-col cols="12" md="4"  class="py-0">
                <ReferralFormLabel required>Plan end date</ReferralFormLabel>
                <ReferralDateInput required v-model="planDetails.endDate" :rules="[planEndDateIsAfterStartDate]">
                  <template #actions>
                    <v-btn
                        v-if="planDetails.startDate"
                        variant="tonal"
                        color="primary"
                        block
                        @click="planDetails.endDate = new Date(planDetails.startDate.getTime() + 365 * 24 * 60 * 60 * 1000)"
                    >
                      Set to 1 year from start date
                    </v-btn>
                  </template>
                </ReferralDateInput>
              </v-col>
              <v-divider/>
            </v-row>
            <v-row>
              <v-col cols="12" class="py-0 mb-1 text-overline">
                Funding Categories
              </v-col>
            </v-row>
            <ReferralFundingSelector v-model="planDetails.fundingCategories"/>
            <v-row>
              <v-divider/>
              <v-col cols="12">
                <ReferralFormLabel required>How is the plan managed? </ReferralFormLabel>
                <v-radio-group v-model="planDetails.managementType" inline hide-details class="text-h6 mt-2" color="primary" :rules="[rules.required]">
                  <v-radio label="Plan Managed" value="Plan Managed" class="pr-5"></v-radio>
                  <v-radio label="Self Managed" value="Self Managed" class="pr-5"></v-radio>
                  <v-radio label="Agency Managed" value="Agency Managed" class="pr-5"></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="12">
                <ReferralFormLabel required>Is this referral urgent? </ReferralFormLabel>
                <v-radio-group v-model="planDetails.isUrgent" inline hide-details class="text-h6 mt-2" color="primary" :rules="[rules.requiredTrueFalse]">
                  <v-radio label="Yes" :value="true" class="pr-5"></v-radio>
                  <v-radio label="No" :value="false" class="pr-5"></v-radio>
                </v-radio-group>
              </v-col>

              <v-col cols="12" v-if="planDetails.isUrgent">
                <v-textarea v-model="planDetails.urgentDetails" required placeholder="Please provide details" :rules="[rules.required]"/>
              </v-col>
            </v-row>
          </v-form>
        </template>

        <template #item.3>
          <v-form ref="form3" class="mt-4">
            <ReferralContact
                v-if="planDetails.managementType === 'Plan Managed'"
                v-model="planManagerContact"
                title="Plan Manager Details"/>

            <v-row>
              <v-col cols="12">
                <ReferralFormLabel required>Do you have a support coordinator? </ReferralFormLabel>
                <v-radio-group v-model="hasSupportCoord" inline hide-details class="text-h6 mt-2" color="primary" :rules="[rules.requiredTrueFalse]">
                  <v-radio label="Yes" :value="true" class="pr-5"></v-radio>
                  <v-radio label="No" :value="false" class="pr-5"></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
            <ReferralContact v-if="hasSupportCoord" v-model="supportCoordinatorContact" title="Support Coordinator Details"/>

            <v-row>
              <v-col cols="12">
                <ReferralFormLabel required>Will the participant be signing/consenting to the service agreement? </ReferralFormLabel>
                <v-radio-group v-model="participantSigning" inline hide-details class="text-h6 mt-2" color="primary" :rules="[rules.requiredTrueFalse]">
                  <v-radio label="Yes" :value="true" class="pr-5"></v-radio>
                  <v-radio label="No" :value="false" class="pr-5"></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
            <ReferralContact v-if="participantSigning === false" v-model="signatoryContact" related title="Signatory Contact Details"/>

            <v-row>
              <v-col cols="12">
                <ReferralFormLabel required>Will the participant be the person to book appointments? </ReferralFormLabel>
                <v-radio-group v-model="participantBooking" inline hide-details class="text-h6 mt-2" color="primary" :rules="[rules.requiredTrueFalse]">
                  <v-radio label="Yes" :value="true" class="pr-5"></v-radio>
                  <v-radio label="No" :value="false" class="pr-5"></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
            <ReferralContact v-if="participantBooking === false" v-model="bookingContact" related title="Booking Contact Details"/>

          </v-form>
        </template>

        <template #item.4>
          <v-form ref="form4" class="mt-4">
            <v-alert class="mb-2" variant="tonal" color="secondary">Please answer as many as you can — too many 'unsure' responses might delay starting your appointments</v-alert>

            <ReferralYesNoUnsure
                v-model="riskAssessment.environmentSafe"
                v-model:details="riskAssessment.environmentSafeDetails"
                :show-details="riskAssessment.environmentSafe === 'no'"
                label="Is the environment a safe place to provide intervention?"
                required/>

            <ReferralYesNoUnsure
                v-model="riskAssessment.dangerousAnimals"
                v-model:details="riskAssessment.dangerousAnimalsDetails"
                :show-details="riskAssessment.dangerousAnimals === 'yes'"
                label="Are you aware of any dangerous dogs/animals on the property?"
                required/>

            <ReferralYesNoUnsure
                v-model="riskAssessment.drugUserAttending"
                v-model:details="riskAssessment.drugUserAttendingDetails"
                label="Will anyone who has used/uses drugs or alcohol be present at the session?"
                required/>

            <ReferralYesNoUnsure
                v-model="riskAssessment.participantSmoker"
                v-model:details="riskAssessment.participantSmokerDetails"
                label="Does the participant smoke tobacco?"
                required/>

            <ReferralYesNoUnsure
                v-model="riskAssessment.participantAggression"
                v-model:details="riskAssessment.participantAggressionDetails"
                :show-details="riskAssessment.participantAggression === 'yes'"
                label="Does the participant have a history of aggression (physical or verbal)?"
                required/>

            <ReferralYesNoUnsure
                v-model="riskAssessment.participantAggressionTriggers"
                v-model:details="riskAssessment.participantAggressionTriggersDetails"
                :show-details="riskAssessment.participantAggressionTriggers === 'yes'"
                label="Are there any triggers for aggression that we should be aware of?"
                required/>

            <ReferralYesNoUnsure
                v-model="riskAssessment.participantStressTriggers"
                v-model:details="riskAssessment.participantStressTriggersDetails"
                :show-details="riskAssessment.participantStressTriggers === 'yes'"
                label="Are there any triggers for stress or anxiety that we should be aware of?"
                required/>

            <ReferralYesNoUnsure
                v-model="riskAssessment.shouldHaveSecondPerson"
                v-model:details="riskAssessment.shouldHaveSecondPersonDetails"
                :show-details="riskAssessment.shouldHaveSecondPerson === 'yes'"
                label="Is there any reason a second person should attend the session?"
                required/>

            <ReferralYesNoUnsure
                v-model="riskAssessment.anyCriminals"
                v-model:details="riskAssessment.anyCriminalsDetails"
                :show-details="riskAssessment.anyCriminals === 'yes'"
                label="Does the participant or person(s) on the premises have a criminal record?"
                required/>

            <v-row>
              <v-col cols="12">
                <ReferralFormLabel>Is there any other relevant information you would like to provide?</ReferralFormLabel>
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="riskAssessment.anyOtherRelevantInformationDetails" label="Please provide details"/>
              </v-col>
            </v-row>
          </v-form>
        </template>

        <template #item.5>
          <v-row>
            <v-col cols="12" class="pb-0">
              <ReferralFormLabel>Would you like a confirmation email to any of the following?</ReferralFormLabel>
            </v-col>
            <v-col cols="12">
              <div class="d-flex flex-wrap align-center mt-2">
                <v-checkbox v-model="confirmationEmails" v-for="email in allContactEmails" :label="`${email.email} (${email.title})`" :value="email.email" :key="email.email" class="pr-4 text-h6" hide-details></v-checkbox>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="otherConfirmationEmail" label="Other email" type="email" inputmode="email" :rules="[rules.optionalEmail]"/>
            </v-col>
          </v-row>
          
          <v-row v-if="successfullySubmitted" class="pb-3">
            <v-col cols="12">
              <v-alert variant="tonal" color="success">
                Referral successfully submitted. We're looking forward to working with you. You can safely navigate away from this page now.
              </v-alert>
            </v-col>
          </v-row>
        </template>

        <template #next>
          <div class="d-flex gap-2 align-center">
            <v-icon v-if="!isNextButtonActive" icon="mdi-help" size="sm" color="grey" v-tooltip="'You have missed some of the required fields. This button will change colour when all the required fields are filled'"></v-icon>
            <v-btn color="secondary" variant="tonal" :disabled="!isNextButtonActive || isSubmittingForm || successfullySubmitted" @click="handleNextBtnClick" :loading="isSubmittingForm">
              {{stepperStep === 5 ? 'Submit Referral' : 'Next'}}
            </v-btn>  
          </div>
        </template>

      </v-stepper>

      <ReferralSubmissionError v-model="referralErrorShowing" />
    </v-main>
  </v-layout>
</template>

<style>

.v-field:has(input[required]) .v-label, .v-field:has(textarea[required]) .v-label {
  padding-inline-end: 8px;
}
.v-field:has(input[required]) .v-label::after, .v-field:has(textarea[required]) .v-label::after {
  position: absolute;
  content: "*";
  color: var(--bs-danger);
  top: 0;
  inset-inline-end: 0; /* to support also RTL direction */
}


</style>

<style scoped>

:deep(.v-stepper-header) {
  box-shadow: none !important;
}

:deep(.v-stepper-item.v-stepper-item--selected) {
  color: rgb(var(--v-theme-secondary));

  .v-avatar {
    background: rgb(var(--v-theme-secondary));
  }
}

:deep(.v-stepper-item:first-child) {
  padding-left: 0;
}

:deep(.v-window.v-stepper-window)  {
  margin: 5px;
}

</style>
